import React from 'react';
import PropTypes from 'prop-types';

import Select from '../../../../components/Select';
import { TextInput } from '../../../../components/Inputs/TextInput';
import Strings from '../../../../Strings';
import { FAIL_AT_OPTIONS, MODE_HTTP_ERROR, MODE_RESULT, PATIENT_MODES, RESULT_CODES, toOptions } from './constants';
import styles from './AccessSimulator.module.scss';

const MODE_LABELS = {
  result: Strings.accessSimModeResult,
  http_error: Strings.accessSimModeHttpError,
  still_processing_forever: Strings.accessSimModeStillProcessing,
  no_record: Strings.accessSimModeNoRecord,
};

/**
 * One scripted answer of the ACCESS simulator: a row of access_sim_responses
 * (per patient) or of access_sim_org_defaults, which is the only
 * place the 'no_record' mode exists.
 *
 * Deliberately dumb: `value` is the draft, `onChange` receives the whole next
 * draft, and `scriptIsValid` below decides whether Save may be enabled.
 */
function ScriptEditor({ id, operation, value, onChange, modes, showTimesRemaining, disabled }) {
  const update = next => onChange({ ...value, ...next });
  const resultCodes = RESULT_CODES[operation] || [];

  return (
    <div className={styles.script}>
      <label className={styles.scriptField} htmlFor={`${id}-mode`}>
        {Strings.accessSimMode}
        <Select
          id={`${id}-mode`}
          label={Strings.accessSimMode}
          data={modes.map(mode => ({ value: mode, label: MODE_LABELS[mode] || mode }))}
          value={value.mode}
          onChange={option => update({ mode: option?.value })}
          disabled={disabled}
        />
      </label>

      {value.mode === MODE_RESULT && (
        <>
          <label className={styles.scriptField} htmlFor={`${id}-result-code`}>
            {Strings.accessSimResultCode}
            <Select
              id={`${id}-result-code`}
              label={Strings.accessSimResultCode}
              data={toOptions(resultCodes)}
              value={value.result_code || ''}
              onChange={option => update({ result_code: option?.value })}
              disabled={disabled}
            />
            {!value.result_code && <span className="validation-message">{Strings.required}</span>}
          </label>
          <label className={styles.scriptField} htmlFor={`${id}-result-display`}>
            {Strings.accessSimResultDisplay}
            <TextInput
              id={`${id}-result-display`}
              type="text"
              value={value.result_display || ''}
              placeholder={value.result_code || ''}
              onChange={event => update({ result_display: event.target.value })}
              disabled={disabled}
            />
          </label>
        </>
      )}

      {value.mode === MODE_HTTP_ERROR && (
        <>
          <label className={styles.scriptField} htmlFor={`${id}-http-status`}>
            {Strings.accessSimHttpStatus}
            <TextInput
              id={`${id}-http-status`}
              type="number"
              min="400"
              max="599"
              value={value.http_status ?? ''}
              onChange={event => update({ http_status: event.target.value === '' ? null : Number(event.target.value) })}
              disabled={disabled}
            />
            {!value.http_status && <span className="validation-message">{Strings.required}</span>}
          </label>
          <label className={styles.scriptField} htmlFor={`${id}-fail-at`} data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimFailAtTooltip}>
            {Strings.accessSimFailAt}
            <Select
              id={`${id}-fail-at`}
              label={Strings.accessSimFailAt}
              data={toOptions(FAIL_AT_OPTIONS)}
              value={value.fail_at || 'accept'}
              onChange={option => update({ fail_at: option?.value })}
              disabled={disabled}
            />
          </label>
          <label className={`${styles.scriptField} ${styles.scriptFieldWide}`} htmlFor={`${id}-error-body`}>
            {Strings.accessSimErrorBody}
            <textarea
              id={`${id}-error-body`}
              rows={2}
              value={value.error_body || ''}
              onChange={event => update({ error_body: event.target.value })}
              disabled={disabled}
            />
          </label>
        </>
      )}

      <label className={styles.scriptField} htmlFor={`${id}-poll-202-count`}>
        <span data-tooltip-id="tooltip" data-tooltip-content={Strings.accessSimPoll202Tooltip}>{Strings.accessSimPoll202}</span>
        <TextInput
          id={`${id}-poll-202-count`}
          type="number"
          min="0"
          value={value.poll_202_count ?? 0}
          onChange={event => update({ poll_202_count: event.target.value === '' ? null : Number(event.target.value) })}
          disabled={disabled}
        />
      </label>

      {showTimesRemaining && (
        <label className={styles.scriptField} htmlFor={`${id}-times-remaining`}>
          {Strings.accessSimTimesRemaining}
          <TextInput
            id={`${id}-times-remaining`}
            type="number"
            min="1"
            value={value.times_remaining ?? ''}
            onChange={event => update({ times_remaining: event.target.value === '' ? null : Number(event.target.value) })}
            disabled={disabled}
          />
        </label>
      )}
    </div>
  );
}

// Mirrors the checks of accessSetPatientResponse / accessSetOrgDefault so the
// Save button cannot produce a 400.
export const scriptIsValid = script => {
  if (!script?.mode) return false;
  if (script.mode === MODE_RESULT && !script.result_code) return false;
  if (script.mode === MODE_HTTP_ERROR && !script.http_status) return false;
  return true;
};

ScriptEditor.propTypes = {
  id: PropTypes.string.isRequired,
  operation: PropTypes.string,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  modes: PropTypes.arrayOf(PropTypes.string),
  showTimesRemaining: PropTypes.bool,
  disabled: PropTypes.bool,
};

ScriptEditor.defaultProps = {
  modes: PATIENT_MODES,
  showTimesRemaining: true,
};

export default ScriptEditor;
